import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule, MatMenu } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { RecordingAccess } from 'src/app/models/space-recording';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { PencilMenuItemComponent } from 'src/app/standalones/components/pencil-menu-item/pencil-menu-item.component';
import { TooltipDirective } from 'src/app/standalones/directives/pncl-tooltip.directive';
import {
  RecordingAccessOption,
  SpaceRecordingService,
} from 'src/app/services/space-recording.service';

@UntilDestroy()
@Component({
  selector: 'app-recording-access-menu',
  templateUrl: './recording-access-menu.component.html',
  styleUrls: ['./recording-access-menu.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'recordingAccessMenu',
  imports: [
    CommonModule,
    MatMenuModule,
    TranslateModule,
    PencilMenuItemComponent,
    TooltipDirective,
  ],
})
export class RecordingAccessMenuComponent implements OnInit, OnChanges {
  readonly IconTypes = IconTypes;
  readonly RecordingAccess = RecordingAccess;

  @ViewChild(MatMenu) menu!: MatMenu;

  @Input() selectedValue: RecordingAccess | undefined;
  @Input() includeSubtitles: boolean = false;
  @Input() enablePublicAccess: boolean = true;
  @Input() enableAdminAccess: boolean = false;

  @Output() optionSelected = new EventEmitter<RecordingAccess>();

  options$ = new BehaviorSubject<RecordingAccessOption[]>([]);

  constructor(private spaceRecordingService: SpaceRecordingService) {}

  ngOnInit(): void {
    this.updateOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['includeSubtitles']) {
      this.updateOptions();
    }
  }

  private updateOptions(): void {
    const options = this.spaceRecordingService.getRecordingAccessOptions({
      includeSubtitles: this.includeSubtitles,
    });
    const optionsWithDisabled = options.map((option) => ({
      ...option,
      disabled: this.isOptionDisabled(option),
    }));
    this.options$.next(optionsWithDisabled);
  }

  onOptionClick(option: RecordingAccessOption): void {
    if (!this.isOptionDisabled(option)) {
      this.optionSelected.emit(option.value);
    }
  }

  private isOptionDisabled(option: RecordingAccessOption): boolean {
    return (
      (option.value === RecordingAccess.PUBLIC && !this.enablePublicAccess) ||
      (option.value === RecordingAccess.ADMINS && !this.enableAdminAccess)
    );
  }
}
